import { FC, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Autocomplete,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from "@mui/material";
import { CommentDto, Country } from "../../../../../../api/Api";
import { CommentsSection } from "../../../../../../components/lifecycle/commentsSection";
import { LifecycleDialog } from "../../../../../../components/lifecycle/lifecycleDialog";
import { SignOffNoteField } from "../../../../../../components/lifecycle/signOffNoteField";
import { useCountryAssignees } from "../../../../dataHooks/countryQueries";
import { countryAttachmentApi } from "../../../countryAttachmentApi";
import { useCountryLifecycle } from "../../useCountryLifecycle";

type Props = {
  specificCountry: Country;
};

type FormData = {
  includedInExistingTradeCapture: string;
  includedInExistingTradeCaptureAssignee: string;
  newTradeCaptureStatus: string;
  newTradeCaptureStatusAssignee: string;
  signOffNote: string;
  comments: CommentDto[];
};

export const Tech: FC<Props> = ({ specificCountry }) => {
  const params = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const lifecycle = specificCountry.techLifecycle;

  const [formData, setFormData] = useState<FormData>({
    includedInExistingTradeCapture:
      lifecycle?.includedInExistingTradeCapture || "",
    includedInExistingTradeCaptureAssignee:
      lifecycle?.includedInExistingTradeCaptureAssignee || "",
    newTradeCaptureStatus: lifecycle?.newTradeCaptureStatus || "",
    newTradeCaptureStatusAssignee:
      lifecycle?.newTradeCaptureStatusAssignee || "",
    signOffNote: lifecycle?.signOffNote || "",
    comments: lifecycle?.comments || []
  });

  const handleClose = () => navigate(`/countries/country/${params.id}`);

  const {
    handleSave,
    handleComplete,
    isSaving,
    progressOverride,
    currentComment,
    setCurrentComment,
    addComment,
    deleteComment
  } = useCountryLifecycle({
    country: specificCountry,
    lifecycle: "techLifecycle",
    formData,
    setFormData,
    onClose: handleClose
  });

  const techAssignees = useCountryAssignees("technology");

  const handleChange = (field: keyof FormData, value: string) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const open = location.pathname.includes("tech");

  const needsNewCapture = formData.includedInExistingTradeCapture === "no";

  const canComplete = Boolean(
    formData.includedInExistingTradeCapture &&
      (!needsNewCapture || formData.newTradeCaptureStatus)
  );

  return (
    <LifecycleDialog
      open={open}
      onClose={handleClose}
      title="Technology"
      progressOverride={progressOverride}
      maxWidth="md"
      attachmentApi={countryAttachmentApi}
      actions={{
        onClose: handleClose,
        onSave: () => handleSave(),
        onComplete: () => handleComplete(),
        canComplete,
        isSaving
      }}
    >
      <Box sx={{ paddingTop: 2 }} gap={2} display="flex" flexDirection="column">
        <Box display="flex" gap={2}>
          <FormControl size="medium" required sx={{ flex: 1 }}>
            <InputLabel>Included in existing trade capture</InputLabel>
            <Select
              label="Included in existing trade capture"
              value={formData.includedInExistingTradeCapture}
              onChange={(e) =>
                handleChange("includedInExistingTradeCapture", e.target.value)
              }
            >
              <MenuItem value="yes">Yes</MenuItem>
              <MenuItem value="no">No</MenuItem>
            </Select>
          </FormControl>

          <Autocomplete
            sx={{ flex: 1 }}
            options={techAssignees.data || []}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={
              techAssignees.data?.find(
                (a) =>
                  a.email === formData.includedInExistingTradeCaptureAssignee
              ) || null
            }
            onChange={(_, newValue) =>
              handleChange(
                "includedInExistingTradeCaptureAssignee",
                newValue?.email || ""
              )
            }
            renderInput={(params) => (
              <TextField {...params} label="Assignee" size="medium" />
            )}
          />
        </Box>

        {needsNewCapture && (
          <Box display="flex" gap={2}>
            <FormControl size="medium" required sx={{ flex: 1 }}>
              <InputLabel>New trade capture developed</InputLabel>
              <Select
                label="New trade capture developed"
                value={formData.newTradeCaptureStatus}
                onChange={(e) =>
                  handleChange("newTradeCaptureStatus", e.target.value)
                }
              >
                <MenuItem value="initiated">Initiated</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>

            <Autocomplete
              sx={{ flex: 1 }}
              options={techAssignees.data || []}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={
                techAssignees.data?.find(
                  (a) => a.email === formData.newTradeCaptureStatusAssignee
                ) || null
              }
              onChange={(_, newValue) =>
                handleChange(
                  "newTradeCaptureStatusAssignee",
                  newValue?.email || ""
                )
              }
              renderInput={(params) => (
                <TextField {...params} label="Assignee" size="medium" />
              )}
            />
          </Box>
        )}

        <SignOffNoteField
          value={formData.signOffNote}
          onChange={(value) => handleChange("signOffNote", value)}
        />

        <CommentsSection
          loading={isSaving}
          comments={formData.comments}
          currentComment={currentComment}
          onCurrentCommentChange={setCurrentComment}
          saveComment={addComment}
          onDeleteComment={deleteComment}
        />
      </Box>
    </LifecycleDialog>
  );
};
